import { RefObject, useEffect, useMemo, useState } from 'react';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';

import {
  ScriptsDialog,
  ScriptsModalContent,
  ScriptsModalHeader,
  HeaderTitleRow,
  HeaderActionsRow,
  ModalTitle,
  CloseButton,
  ScriptsModalBody,
  ScriptsModalActions,
  CancelButton,
  PrimaryButton,
} from '../AssistantScriptDialog.styled';
import {
  WizardHeaderTop,
  WizardStepCounter,
  WizardProgressRow,
  WizardProgressSegment,
} from './styled';
import QuestionShell from '../QuestionShell';
import QuestionBody from '../QuestionBody';
import { QUESTION_META, getVisibleQuestions } from '../questions';
import type { ScriptDialogState } from '../types';

interface CreateWizardProps {
  open: boolean;
  state: ScriptDialogState;
  paperRef?: RefObject<HTMLDivElement | undefined>;
  onClose: () => void;
}

const CreateWizard = ({ open, state, paperRef, onClose }: CreateWizardProps) => {
  const { t } = useTranslation('common');
  const [questionIndex, setQuestionIndex] = useState(0);

  const visibleQuestions = useMemo(
    () => getVisibleQuestions(state.triggerType),
    [state.triggerType],
  );
  const qi = Math.min(questionIndex, visibleQuestions.length - 1);
  const currentKey = visibleQuestions[qi];
  const meta = QUESTION_META[currentKey];
  const isLast = qi === visibleQuestions.length - 1;

  // Same as EditSummary's "Edit languages" — arriving at the languages step
  // (whether via Continue or Back) shouldn't land on a preselected tab.
  const [languagesTabPicked, setLanguagesTabPicked] = useState(false);

  useEffect(() => {
    if (currentKey === 'languages') {
      setLanguagesTabPicked(false);
    }
  }, [currentKey]);

  // The steps list isn't in the DOM yet on the click that first picks a
  // language (see QuestionBody/EditSummary) — scroll to it once it mounts.
  useEffect(() => {
    if (currentKey === 'languages' && languagesTabPicked) {
      state.stepsHeaderRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  }, [currentKey, languagesTabPicked, state.stepsHeaderRef]);

  const goBack = () => {
    if (qi > 0) {
      setQuestionIndex(qi - 1);
    }
  };

  const goNext = () => {
    if (isLast) {
      state.formik.handleSubmit();
      return;
    }

    setQuestionIndex(qi + 1);
  };

  return (
    <ScriptsDialog
      open={open}
      onClose={(_, reason) => {
        if (reason === 'backdropClick') {
          return;
        }

        onClose();
      }}
      fullWidth
      $theme={state.modalTheme}
      PaperProps={{ ref: paperRef }}
    >
      <ScriptsModalContent>
        <ScriptsModalHeader sx={{ flexDirection: 'column', alignItems: 'stretch' }}>
          <WizardHeaderTop>
            <HeaderTitleRow>
              <ArticleOutlinedIcon
                sx={{ color: 'var(--am-icon-accent)', fontSize: 22 }}
              />
              <ModalTitle sx={{ fontWeight: 400 }}>
                {t('assistant.scriptDialog.createScript')}
              </ModalTitle>
            </HeaderTitleRow>
            <HeaderActionsRow>
              <WizardStepCounter>
                {t('assistant.scriptDialog.wizard.stepOf', {
                  current: qi + 1,
                  total: visibleQuestions.length,
                })}
              </WizardStepCounter>
              <CloseButton
                onClick={onClose}
                size="small"
                data-testid="assistant-script-wizard-close-button"
              >
                <CloseIcon />
              </CloseButton>
            </HeaderActionsRow>
          </WizardHeaderTop>
          <WizardProgressRow>
            {visibleQuestions.map((key, index) => (
              <WizardProgressSegment key={key} $active={index <= qi} />
            ))}
          </WizardProgressRow>
        </ScriptsModalHeader>

        <ScriptsModalBody ref={state.modalBodyRef}>
          <QuestionShell
            eyebrowKey={meta.eyebrowKey}
            titleKey={meta.titleKey}
            helperKey={meta.helperKey}
          >
            <QuestionBody
              which={currentKey}
              state={state}
              languagesForceNoSelection={
                currentKey === 'languages' && !languagesTabPicked
              }
              onLanguagesTabPick={() => setLanguagesTabPicked(true)}
            />
          </QuestionShell>
        </ScriptsModalBody>

        <ScriptsModalActions>
          <CancelButton
            onClick={qi > 0 ? goBack : onClose}
            data-testid={
              qi > 0
                ? 'assistant-script-wizard-back-button'
                : 'assistant-script-wizard-cancel-button'
            }
          >
            {qi > 0
              ? t('assistant.scriptDialog.wizard.back')
              : t('assistant.scriptDialog.cancel')}
          </CancelButton>
          <PrimaryButton
            onClick={goNext}
            disabled={
              (isLast && state.scriptSteps.length === 0) || state.isSubmitting
            }
            data-testid={
              isLast
                ? 'assistant-script-wizard-submit-button'
                : 'assistant-script-wizard-continue-button'
            }
          >
            {isLast
              ? t('assistant.scriptDialog.createScript')
              : t('assistant.scriptDialog.wizard.continue')}
          </PrimaryButton>
        </ScriptsModalActions>
      </ScriptsModalContent>
    </ScriptsDialog>
  );
};

export default CreateWizard;
